import { uiSurface } from "shared/ui/uiSurface"
import { Save } from "@mui/icons-material"
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Snackbar,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material"
import { FC, useCallback, useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import {
  useCreateConfigurationMutation,
  useGetConfigurationsQuery,
  useUpdateConfigurationMutation,
} from "../../apis/appApi"

type NotificationFormState = {
  NOTIFICATION_SMS_ENABLED: string
  NOTIFICATION_EMAIL_ENABLED: string
  NOTIFICATION_SMS_READY: string
  NOTIFICATION_SMS_OUT_FOR_DELIVERY: string
  NOTIFICATION_SMS_DELIVERED: string
  NOTIFICATION_SMS_FAILED: string
}

const DEFAULTS: NotificationFormState = {
  NOTIFICATION_SMS_ENABLED: "false",
  NOTIFICATION_EMAIL_ENABLED: "false",
  NOTIFICATION_SMS_READY: "",
  NOTIFICATION_SMS_OUT_FOR_DELIVERY: "",
  NOTIFICATION_SMS_DELIVERED: "",
  NOTIFICATION_SMS_FAILED: "",
}

const NOTIFICATION_KEYS = Object.keys(
  DEFAULTS,
) as (keyof NotificationFormState)[]

const AVAILABLE_VARIABLES = [
  "{clientName}",
  "{reference}",
  "{providerName}",
  "{eta}",
]

export const NotificationsTab: FC = () => {
  const { t } = useTranslation()
  const { data: configurations, isLoading } = useGetConfigurationsQuery()
  const [createConfiguration] = useCreateConfigurationMutation()
  const [updateConfiguration] = useUpdateConfigurationMutation()

  const [form, setForm] = useState<NotificationFormState>(DEFAULTS)
  const [snackbar, setSnackbar] = useState<{
    open: boolean
    severity: "success" | "error"
    message: string
  }>({ open: false, severity: "success", message: "" })
  const [saving, setSaving] = useState(false)

  // Build a map of existing configs keyed by name
  const configMap = useMemo(() => {
    const map: Record<string, { id: number; value: string }> = {}
    if (configurations) {
      for (const cfg of configurations) {
        if (
          NOTIFICATION_KEYS.includes(cfg.name as keyof NotificationFormState)
        ) {
          map[cfg.name] = { id: cfg.id, value: cfg.value ?? "" }
        }
      }
    }
    return map
  }, [configurations])

  // Load values from configs on mount / data change
  const loadedValues = useMemo(() => {
    const values = { ...DEFAULTS }
    for (const key of NOTIFICATION_KEYS) {
      if (configMap[key]) {
        values[key] = configMap[key].value
      }
    }
    return values
  }, [configMap])

  useEffect(() => {
    setForm(loadedValues)
  }, [loadedValues])

  const hasChanges = useMemo(
    () => NOTIFICATION_KEYS.some((key) => form[key] !== loadedValues[key]),
    [form, loadedValues],
  )

  const setField = useCallback(
    (key: keyof NotificationFormState, value: string) => {
      setForm((prev) => ({ ...prev, [key]: value }))
    },
    [],
  )

  const toggleField = useCallback((key: keyof NotificationFormState) => {
    setForm((prev) => ({
      ...prev,
      [key]: prev[key] === "true" ? "false" : "true",
    }))
  }, [])

  const handleSave = async () => {
    setSaving(true)
    try {
      for (const key of NOTIFICATION_KEYS) {
        const existing = configMap[key]
        if (existing) {
          await updateConfiguration({
            id: existing.id,
            body: { value: form[key] },
          })
        } else {
          await createConfiguration({
            name: key,
            value: form[key],
            enabled: true,
          })
        }
      }
      setSnackbar({
        open: true,
        severity: "success",
        message: t("configuration.notifications.saved"),
      })
    } catch {
      setSnackbar({
        open: true,
        severity: "error",
        message: t("configuration.notifications.saveError"),
      })
    } finally {
      setSaving(false)
    }
  }

  if (isLoading) {
    return (
      <Box
        data-testid="notifications-loading"
        sx={{ display: "flex", justifyContent: "center", py: 8 }}
      >
        <CircularProgress />
      </Box>
    )
  }

  const templateFields: {
    key: keyof NotificationFormState
    labelKey: string
    placeholderKey: string
  }[] = [
    {
      key: "NOTIFICATION_SMS_READY",
      labelKey: "configuration.notifications.smsReady",
      placeholderKey: "configuration.notifications.smsReadyPlaceholder",
    },
    {
      key: "NOTIFICATION_SMS_OUT_FOR_DELIVERY",
      labelKey: "configuration.notifications.smsOutForDelivery",
      placeholderKey:
        "configuration.notifications.smsOutForDeliveryPlaceholder",
    },
    {
      key: "NOTIFICATION_SMS_DELIVERED",
      labelKey: "configuration.notifications.smsDelivered",
      placeholderKey: "configuration.notifications.smsDeliveredPlaceholder",
    },
    {
      key: "NOTIFICATION_SMS_FAILED",
      labelKey: "configuration.notifications.smsFailed",
      placeholderKey: "configuration.notifications.smsFailedPlaceholder",
    },
  ]

  return (
    <Container {...uiSurface("UI-DLV2-183")} data-testid="notifications-tab" maxWidth="md" sx={{ py: 4 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" fontWeight={600} color="text.primary">
          {t("configuration.notifications.title")}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {t("configuration.notifications.subtitle")}
        </Typography>
      </Box>

      <Stack spacing={3}>
        {/* General Section */}
        <Box>
          <Typography
            variant="subtitle1"
            fontWeight={700}
            color="text.primary"
            sx={{ mb: 2 }}
          >
            {t("configuration.notifications.general")}
          </Typography>
          <Stack spacing={1}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <Box>
                <Typography variant="body2" fontWeight={500}>
                  {t("configuration.notifications.smsEnabled")}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t("configuration.notifications.smsEnabledDescription")}
                </Typography>
              </Box>
              <Switch
                data-testid="notifications-sms-enabled-switch"
                checked={form.NOTIFICATION_SMS_ENABLED === "true"}
                onChange={() => toggleField("NOTIFICATION_SMS_ENABLED")}
              />
            </Stack>

            <Divider sx={{ my: 1 }} />

            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <Box>
                <Typography variant="body2" fontWeight={500}>
                  {t("configuration.notifications.emailEnabled")}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t("configuration.notifications.emailEnabledDescription")}
                </Typography>
              </Box>
              <Switch
                data-testid="notifications-email-enabled-switch"
                checked={form.NOTIFICATION_EMAIL_ENABLED === "true"}
                onChange={() => toggleField("NOTIFICATION_EMAIL_ENABLED")}
              />
            </Stack>
          </Stack>
        </Box>

        <Divider />

        {/* SMS Templates Section */}
        <Box>
          <Typography
            variant="subtitle1"
            fontWeight={700}
            color="text.primary"
            sx={{ mb: 0.5 }}
          >
            {t("configuration.notifications.templates")}
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: 2 }}
          >
            {t("configuration.notifications.templatesDescription")}
          </Typography>

          <Box sx={{ mb: 3 }}>
            <Typography
              variant="caption"
              fontWeight={600}
              color="text.secondary"
              sx={{ mb: 1, display: "block" }}
            >
              {t("configuration.notifications.availableVariables")}
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {AVAILABLE_VARIABLES.map((variable) => (
                <Chip
                  key={variable}
                  label={variable}
                  size="small"
                  variant="outlined"
                  sx={{ fontFamily: "monospace", fontSize: "0.8rem" }}
                />
              ))}
            </Stack>
          </Box>

          <Stack spacing={3}>
            {templateFields.map(({ key, labelKey, placeholderKey }) => (
              <TextField
                key={key}
                data-testid={`notifications-${key.toLowerCase().replace(/_/g, "-")}-input`}
                label={t(labelKey)}
                placeholder={t(placeholderKey)}
                value={form[key]}
                onChange={(e) => setField(key, e.target.value)}
                multiline
                rows={3}
                fullWidth
                helperText={`${t("configuration.notifications.availableVariables")}: ${AVAILABLE_VARIABLES.join(", ")}`}
              />
            ))}
          </Stack>
        </Box>

        <Divider />

        {/* Save Button */}
        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <Button
            data-testid="notifications-save-btn"
            variant="contained"
            startIcon={saving ? <CircularProgress size={16} /> : <Save />}
            onClick={handleSave}
            disabled={!hasChanges || saving}
            sx={{ borderRadius: 2, textTransform: "none", px: 4 }}
          >
            {t("save")}
          </Button>
        </Box>
      </Stack>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  )
}
